import { useEffect, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  NOTIFICATIONS_PER_PAGE,
} from "../features/notifications/notificationsActions";
import { closeChat } from "../features/orderChat/orderChatSlice";

// Where a line takes the customer: the Orders page narrowed to that one
// order, plus its chat for a message line. `order_id` is the numeric id the
// history endpoint filters on, never the human order number.
export const getNotificationPath = (notif) => {
  if (notif?.order_id === undefined || notif?.order_id === null) {
    return null;
  }
  const chat = notif?.type === "order_message" ? "&chat=1" : "";
  return `/orders?id=${notif.order_id}${chat}`;
};

// Bell state for the header: badge count, drawer open/closed, the page and
// unread filter of the list, and the click handlers. Mounted once (in the
// header) for a logged-in customer; the first fetch is what seeds the badge.
export default function useNotifications({ enabled = true, onError } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const { items, meta, loading, markAllLoading } =
    useSelector((state) => state?.notifications) || {};

  const unreadCount = meta?.unread_count || 0;
  const totalCount = meta?.count || 0;
  const unreadByType = meta?.unread_by_type || {};

  const buildParams = useCallback((page, unreadOnly) => {
    const params = { page, per_page: NOTIFICATIONS_PER_PAGE };
    if (unreadOnly) {
      params.is_read = false;
    }
    return params;
  }, []);

  // Once on app load (and again after a login) so the badge has a number
  // before the drawer is ever opened.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    dispatch(getNotifications(buildParams(1, false)));
  }, [enabled, dispatch, buildParams]);

  // Fresh page 1 every time the drawer opens; the socket keeps it live while
  // it stays open.
  useEffect(() => {
    if (!enabled || !drawerOpen) {
      return;
    }
    setCurrentPage(1);
    dispatch(getNotifications(buildParams(1, onlyUnread)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const refetch = useCallback(() => {
    dispatch(getNotifications(buildParams(currentPage, onlyUnread)));
  }, [dispatch, buildParams, currentPage, onlyUnread]);

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      dispatch(getNotifications(buildParams(page, onlyUnread)));
    },
    [dispatch, buildParams, onlyUnread]
  );

  const handleToggleUnread = useCallback(
    (checked) => {
      const next = typeof checked === "boolean" ? checked : !onlyUnread;
      setOnlyUnread(next);
      setCurrentPage(1);
      dispatch(getNotifications(buildParams(1, next)));
    },
    [dispatch, buildParams, onlyUnread]
  );

  // 1. mark it read (optimistic), 2. close the drawer, 3. go to the order.
  const handleNotificationClick = useCallback(
    (notif) => {
      if (!notif?.id) {
        return;
      }
      if (!notif?.is_read) {
        dispatch(markNotificationRead({ id: notif.id, is_read: true }))
          .unwrap()
          .catch((err) => {
            if (err?.status === 404) {
              // The line vanished with its order; the slice already dropped
              // it, refill the page.
              dispatch(getNotifications(buildParams(currentPage, onlyUnread)));
              return;
            }
            onErrorRef.current?.(err);
          });
      }
      setDrawerOpen(false);
      const path = getNotificationPath(notif);
      if (path) {
        // A chat drawer left open for another order would sit on top of the
        // order the customer asked for.
        dispatch(closeChat());
        navigate(path);
      }
    },
    [dispatch, navigate, buildParams, currentPage, onlyUnread]
  );

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllNotificationsRead())
      .unwrap()
      .then(() => {
        // With "Only show unread" on, the page just emptied itself.
        if (onlyUnread) {
          setCurrentPage(1);
          dispatch(getNotifications(buildParams(1, true)));
        }
      })
      .catch((err) => {
        onErrorRef.current?.(err);
      });
  }, [dispatch, buildParams, onlyUnread]);

  return {
    notifications: items || [],
    unreadCount,
    unreadByType,
    totalCount,
    perPage: NOTIFICATIONS_PER_PAGE,
    loading: Boolean(loading),
    markAllLoading: Boolean(markAllLoading),
    currentPage,
    drawerOpen,
    openDrawer,
    closeDrawer,
    onlyUnread,
    refetch,
    handlePageChange,
    handleToggleUnread,
    handleNotificationClick,
    handleMarkAllRead,
  };
}
